import { API_BASE_URL, fetchApi } from '@/services/api-client';

export type VisitTagRef = { id: string; label: string; color: string };
export type VisitAttachmentTag = 'before' | 'after' | 'other';

export type VisitAttachment = {
  id: string;
  tag: VisitAttachmentTag;
  originalName: string;
  visibleToFamily: boolean;
};

/** In tile order: a visit reads before → after, and everything else trails behind. */
export const VISIT_ATTACHMENT_TAGS: VisitAttachmentTag[] = ['before', 'after', 'other'];

/** One photo as the detail endpoint describes it — enough to preview and to caption. */
export type VisitAttachmentDetail = VisitAttachment & {
  mimeType: string;
  size: number;
  createdAt: string;
  uploadedByName: string | null;
};

export type InfirmaryVisit = {
  id: string;
  studentId: string;
  studentDisplayName: string | null;
  studentFullName: string | null;
  studentPhotoUrl: string;
  studentGradeLevel: number | null;
  visitDate: string;
  visitTime: string | null;
  typeId: string;
  typeLabel: string | null;
  place: string;
  notes: string | null;
  /** The nurse's message to the family — never the clinical `notes`. */
  familyNote: string | null;
  /** When it was first shared, or null while the family cannot see it at all. */
  sharedWithFamilyAt: string | null;
  earlyDismissalId: string | null;
  tags: VisitTagRef[];
  attachments: VisitAttachment[];
  createdAt: string;
};

/** One visit in full, as `GET /medical/infirmary-visits/:id` returns it. */
export type InfirmaryVisitDetail = Omit<InfirmaryVisit, 'attachments'> & {
  attachments: VisitAttachmentDetail[];
  recordedBy: { id: string; name: string | null } | null;
  updatedAt: string;
  earlyDismissal: {
    id: string;
    dismissalDate: string;
    dismissalTime: string;
    reason: string | null;
    status: string;
  } | null;
};

export type CreateInfirmaryVisitData = {
  studentId: string;
  visitDate: string;
  visitTime?: string;
  typeId: string;
  place?: string;
  notes?: string;
  earlyDismissalId?: string;
  tagIds?: string[];
};

export type UpdateInfirmaryVisitData = Partial<CreateInfirmaryVisitData> & {
  /** Moving the visit to another student deletes its photos; the API refuses the move without this. */
  removeAttachments?: boolean;
};

export type InfirmaryVisitListParams = {
  search?: string;
  typeId?: string;
  tagId?: string;
  page?: number;
  limit?: number;
};

export type InfirmaryVisitListResponse = {
  data: InfirmaryVisit[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export const medicalVisitsApi = {
  /** `to` defaults to `from`, so a single day is just a range of one. */
  listByDateRange: (from: string, to: string, params: InfirmaryVisitListParams = {}) => {
    const query = new URLSearchParams();
    query.append('from', from);
    query.append('to', to || from);
    if (params.search) query.append('search', params.search);
    if (params.typeId) query.append('typeId', params.typeId);
    if (params.tagId) query.append('tagId', params.tagId);
    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    return fetchApi<InfirmaryVisitListResponse>(`/medical/infirmary-visits?${query.toString()}`);
  },
  getById: (id: string) => fetchApi<InfirmaryVisitDetail>(`/medical/infirmary-visits/${id}`),
  listForStudent: (studentId: string) => fetchApi<InfirmaryVisit[]>(`/students/${studentId}/infirmary-visits`),
  create: (data: CreateInfirmaryVisitData) =>
    fetchApi<InfirmaryVisit>('/medical/infirmary-visits', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: UpdateInfirmaryVisitData) =>
    fetchApi<InfirmaryVisit>(`/medical/infirmary-visits/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string) => fetchApi<{ success: true }>(`/medical/infirmary-visits/${id}`, { method: 'DELETE' }),

  uploadPhotos: async (visitId: string, files: File[], tag: VisitAttachmentTag): Promise<InfirmaryVisit> => {
    const form = new FormData();
    files.forEach(f => form.append('files', f, f.name));
    form.append('tag', tag);
    const res = await fetch(`${API_BASE_URL}/medical/infirmary-visits/${visitId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(err.message || 'Upload failed');
    }
    return res.json() as Promise<InfirmaryVisit>;
  },

  updateAttachmentTag: (attId: string, tag: VisitAttachmentTag) =>
    fetchApi<InfirmaryVisit>(`/medical/infirmary-visits/attachments/${attId}`, {
      method: 'PATCH',
      body: JSON.stringify({ tag }),
    }),

  share: (id: string, data: { familyNote?: string | null; attachmentIds: string[] }) =>
    fetchApi<InfirmaryVisit>(`/medical/infirmary-visits/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  unshare: (id: string) => fetchApi<InfirmaryVisit>(`/medical/infirmary-visits/${id}/share`, { method: 'DELETE' }),

  deleteAttachment: (attId: string) =>
    fetchApi<{ success: true }>(`/medical/infirmary-visits/attachments/${attId}`, { method: 'DELETE' }),

  attachmentDownloadUrl: (attId: string): string => `${API_BASE_URL}/medical/infirmary-visits/attachments/${attId}`,
};
